import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Lock, Undo2, RotateCcw } from "lucide-react";
import { api, errMsg, fancallWhen, fmtDate } from "@/lib/api";
import { FancallInfoView } from "@/components/admin/FancallInfoView";

const InfoState = ({ w, onView }) =>
  w.info_submitted ? (
    <button type="button" data-testid={`winner-info-view-${w.raffle_id}-${w.joiner_id}`} onClick={() => onView(w)}
      className="rounded-full border border-emerald-500/40 px-2.5 py-1 text-xs font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/10">
      {w.info_unlocked ? "Info (unlocked)" : "View info"}
    </button>
  ) : (
    <span data-testid={`winner-info-missing-${w.raffle_id}-${w.joiner_id}`} className="text-xs text-zinc-500">Info not sent yet</span>
  );

const WinnerRow = ({ w, onUndo, onView }) => (
  <div data-testid={`winner-row-${w.raffle_id}-${w.joiner_id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-amber-500/15 bg-zinc-900/60 px-4 py-3">
    <Trophy className="h-4 w-4 text-amber-300 shrink-0" />
    <div className="min-w-[75%] sm:min-w-0 flex-1">
      <div className="font-semibold truncate">{w.joiner_name}</div>
      <div className="text-xs text-zinc-400 truncate">Won {w.round_name}{fancallWhen(w) ? ` · ${fancallWhen(w)}` : ""}</div>
    </div>
    {!w.reset_at && (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-200">
        <Lock className="h-3 w-3" /> Locked
      </span>
    )}
    {w.won_at && <span className="font-mono text-xs text-zinc-500">{fmtDate(w.won_at)}</span>}
    <InfoState w={w} onView={onView} />
    {!w.reset_at && <button
      type="button"
      data-testid={`winner-undo-${w.raffle_id}-${w.joiner_id}`}
      onClick={() => onUndo(w)}
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-zinc-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
    >
      <Undo2 className="h-3.5 w-3.5" /> Undo win
    </button>}
  </div>
);

const PastSeasons = ({ rows, ...rest }) => {
  const seasons = [...new Set(rows.map((w) => w.reset_at))].sort().reverse();
  return seasons.map((r) => (
    <div key={r} data-testid={`winner-season-${r}`} className="mt-6">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Past season · reset {fmtDate(r)}</h3>
      <div className="mt-2 space-y-2 opacity-70">
        {rows.filter((w) => w.reset_at === r).map((w) => <WinnerRow key={`${w.raffle_id}-${w.joiner_id}`} w={w} {...rest} />)}
      </div>
    </div>
  ));
};

const ResetButton = ({ count, onReset }) => (
  <button type="button" data-testid="reset-winners-btn" disabled={!count} onClick={onReset}
    className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40">
    <RotateCcw className="h-3.5 w-3.5" /> Reset winners
  </button>
);

export default function WinnerHistory({ refreshKey, onChanged }) {
  const [rows, setRows] = useState(null);
  const [viewing, setViewing] = useState(null);
  const current = rows?.filter((w) => !w.reset_at) ?? [];
  const past = rows?.filter((w) => w.reset_at) ?? [];

  const reset = async () => {
    if (!window.confirm(`Reset ${current.length} winner(s)? They'll move to a past season and everyone can join the raffle again.`)) return;
    try {
      const r = await api.post("/raffle-winners/reset");
      toast.success(`Winners reset (${r.data.reset_count}). Everyone can join the raffle again.`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    api.get("/raffle-winners").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [refreshKey]);

  const undo = async (w) => {
    if (!window.confirm(`Undo ${w.joiner_name}'s win in ${w.round_name}? They'll be able to opt into fancalls again.`)) return;
    try {
      await api.put(`/raffles/${w.raffle_id}/winners/${w.joiner_id}`, { won: false });
      toast.success(`${w.joiner_name}'s win removed`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="mt-8" data-testid="winner-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-base md:text-lg font-bold">Winner History <span data-testid="current-winners-count" className="font-mono text-sm text-zinc-500">({current.length})</span></h2>
        <ResetButton count={current.length} onReset={reset} />
      </div>
      <p className="text-xs text-zinc-500 mt-1">Current-season winners can't opt into later fancall rounds until you reset winners.</p>
      <div className="mt-3 space-y-2">
        {rows === null && <div className="text-zinc-500 text-sm">Loading…</div>}
        {rows && current.length === 0 && <div data-testid="winner-history-empty" className="rounded-xl border border-dashed border-zinc-800 px-4 py-5 text-sm text-zinc-500">No winners this season yet.</div>}
        {current.map((w) => <WinnerRow key={`${w.raffle_id}-${w.joiner_id}`} w={w} onUndo={undo} onView={setViewing} />)}
      </div>
      <PastSeasons rows={past} onUndo={undo} onView={setViewing} />
      <FancallInfoView w={viewing} onClose={() => setViewing(null)} onChanged={onChanged} />
    </section>
  );
}
